// Constantes de la aplicación

// URLs y configuración de API
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || "http://localhost:5000/api",
  UPLOADS_URL: import.meta.env.VITE_UPLOADS_URL || "http://localhost:5000/uploads",
  TIMEOUT: 30000,
};

// Configuración de archivos
export const FILE_CONFIG = {
  MAX_SIZE: parseInt(import.meta.env.VITE_MAX_FILE_SIZE) || 52428800, // 50MB
  SUPPORTED_TYPES: ["pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp"],
  IMAGE_TYPES: ["jpg", "jpeg", "png", "gif", "bmp", "tiff", "webp"],
  DOCUMENT_TYPES: ["pdf", "doc", "docx", "xls", "xlsx"],
};

// Configuración de paginación
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: parseInt(import.meta.env.VITE_DEFAULT_PAGE_SIZE) || 20,
  MAX_PAGE_SIZE: parseInt(import.meta.env.VITE_MAX_PAGE_SIZE) || 100,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};

// Roles de usuario
export const USER_ROLES = {
  ADMIN: "admin",
  OPERATOR: "operator",
  VIEWER: "viewer",
};

// Etiquetas de roles
export const USER_ROLE_LABELS = {
  [USER_ROLES.ADMIN]: "Administrador",
  [USER_ROLES.OPERATOR]: "Operador",
  [USER_ROLES.VIEWER]: "Visualizador",
};

// Colores para roles
export const USER_ROLE_COLORS = {
  [USER_ROLES.ADMIN]: "#d32f2f", // Rojo
  [USER_ROLES.OPERATOR]: "#1976d2", // Azul
  [USER_ROLES.VIEWER]: "#757575", // Gris
};

// Permisos por rol
export const USER_ROLE_PERMISSIONS = {
  [USER_ROLES.ADMIN]: {
    create: true,
    read: true,
    update: true,
    delete: true,
    manageUsers: true,
  },
  [USER_ROLES.OPERATOR]: {
    create: true,
    read: true,
    update: true,
    delete: true, // Los operadores pueden eliminar con tokens de autorización
    manageUsers: false,
  },
  [USER_ROLES.VIEWER]: {
    create: false,
    read: true,
    update: false,
    delete: false,
    manageUsers: false,
  },
};

// Estados operacionales de máquinas
export const MACHINE_STATUS = {
  OPERATIONAL: "operational",
  MAINTENANCE: "maintenance",
  REPAIR: "repair",
  RETIRED: "retired",
};

// Etiquetas de estado operacional
export const MACHINE_STATUS_LABELS = {
  [MACHINE_STATUS.OPERATIONAL]: "Operacional",
  [MACHINE_STATUS.MAINTENANCE]: "Mantenimiento",
  [MACHINE_STATUS.REPAIR]: "Reparación",
  [MACHINE_STATUS.RETIRED]: "Retirada",
};

// Colores para estados operacionales
export const MACHINE_STATUS_COLORS = {
  [MACHINE_STATUS.OPERATIONAL]: "#43a047",
  [MACHINE_STATUS.MAINTENANCE]: "#ffa726",
  [MACHINE_STATUS.REPAIR]: "#ef5350",
  [MACHINE_STATUS.RETIRED]: "#9e9e9e",
};

// Tipos de proceso
export const PROCESS_TYPES = {
  PRODUCTION: "production",
  MAINTENANCE: "maintenance",
  QUALITY: "quality",
  LOGISTICS: "logistics",
  OTHER: "other",
};

// Etiquetas de tipos de proceso
export const PROCESS_TYPE_LABELS = {
  [PROCESS_TYPES.PRODUCTION]: "Producción",
  [PROCESS_TYPES.MAINTENANCE]: "Mantenimiento",
  [PROCESS_TYPES.QUALITY]: "Calidad",
  [PROCESS_TYPES.LOGISTICS]: "Logística",
  [PROCESS_TYPES.OTHER]: "Otro",
};

// Niveles de seguridad
export const SAFETY_LEVELS = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  CRITICAL: "critical",
};

// Etiquetas de niveles de seguridad
export const SAFETY_LEVEL_LABELS = {
  [SAFETY_LEVELS.LOW]: "Bajo",
  [SAFETY_LEVELS.MEDIUM]: "Medio",
  [SAFETY_LEVELS.HIGH]: "Alto",
  [SAFETY_LEVELS.CRITICAL]: "Crítico",
};

// Colores para niveles de seguridad
export const SAFETY_LEVEL_COLORS = {
  [SAFETY_LEVELS.LOW]: "#4caf50",
  [SAFETY_LEVELS.MEDIUM]: "#ff9800",
  [SAFETY_LEVELS.HIGH]: "#ff5722",
  [SAFETY_LEVELS.CRITICAL]: "#f44336",
};

// Categorías de catálogos
export const CATALOG_CATEGORIES = {
  MANUAL: "manual",
  SPECIFICATIONS: "specifications",
  MAINTENANCE: "maintenance",
  SAFETY: "safety",
  INSTALLATION: "installation",
  DRAWINGS: "drawings",
  PHOTOS: "photos",
  OTHER: "other",
};

// Etiquetas de categorías de catálogos
export const CATALOG_CATEGORY_LABELS = {
  [CATALOG_CATEGORIES.MANUAL]: "Manual",
  [CATALOG_CATEGORIES.SPECIFICATIONS]: "Especificaciones",
  [CATALOG_CATEGORIES.MAINTENANCE]: "Mantenimiento",
  [CATALOG_CATEGORIES.SAFETY]: "Seguridad",
  [CATALOG_CATEGORIES.INSTALLATION]: "Instalación",
  [CATALOG_CATEGORIES.DRAWINGS]: "Planos",
  [CATALOG_CATEGORIES.PHOTOS]: "Fotografías",
  [CATALOG_CATEGORIES.OTHER]: "Otro",
};

// Idiomas soportados
export const LANGUAGES = {
  ES: "es",
  EN: "en",
  FR: "fr",
  DE: "de",
  IT: "it",
  PT: "pt",
  OTHER: "other",
};

// Etiquetas de idiomas
export const LANGUAGE_LABELS = {
  [LANGUAGES.ES]: "Español",
  [LANGUAGES.EN]: "Inglés",
  [LANGUAGES.FR]: "Francés",
  [LANGUAGES.DE]: "Alemán",
  [LANGUAGES.IT]: "Italiano",
  [LANGUAGES.PT]: "Portugués",
  [LANGUAGES.OTHER]: "Otro",
};

// Rutas de navegación
export const ROUTES = {
  HOME: "/",
  LOGIN: "/login",
  DASHBOARD: "/dashboard",
  HIERARCHY: "/hierarchy",
  MACHINE_DETAIL: "/machine/:id",
  SEARCH: "/search",
  USERS: "/users",
  PROFILE: "/profile",
  NOT_FOUND: "/404",
};

// Iconos Material-UI para tipos de elementos
export const HIERARCHY_ICONS = {
  plant: "domain",
  process: "settings",
  room: "meeting_room",
  machine: "precision_manufacturing",
  catalog: "folder",
};

// Mensajes de validación
export const VALIDATION_MESSAGES = {
  REQUIRED: "Este campo es obligatorio",
  EMAIL_INVALID: "El email no es válido",
  PASSWORD_MIN_LENGTH: "La contraseña debe tener al menos 6 caracteres",
  PASSWORD_MISMATCH: "Las contraseñas no coinciden",
  FILE_TOO_LARGE: "El archivo es muy grande",
  FILE_TYPE_INVALID: "Tipo de archivo no válido",
  MAX_LENGTH: (max) => `No puede exceder ${max} caracteres`,
  MIN_LENGTH: (min) => `Debe tener al menos ${min} caracteres`,
  NUMERIC_ONLY: "Solo se permiten números",
  POSITIVE_NUMBER: "Debe ser un número positivo",
};

// Configuración de notificaciones
export const NOTIFICATION_CONFIG = {
  DEFAULT_DURATION: 5000,
  SUCCESS_DURATION: 3000,
  ERROR_DURATION: 7000,
  MAX_SNACKBARS: 3,
  ANCHOR_ORIGIN: {
    vertical: "top",
    horizontal: "right",
  },
};

// Configuración de búsqueda
export const SEARCH_CONFIG = {
  MIN_QUERY_LENGTH: 2,
  DEBOUNCE_DELAY: 300,
  MAX_RESULTS: 50,
  HISTORY_SIZE: 10,
};

// Configuración de caché
export const CACHE_CONFIG = {
  DEFAULT_TTL: 300000, // 5 minutos
  HIERARCHY_TTL: 600000, // 10 minutos
  USER_TTL: 900000, // 15 minutos
};

// Breakpoints para responsive design
export const BREAKPOINTS = {
  XS: 0,
  SM: 600,
  MD: 900,
  LG: 1200,
  XL: 1920,
};

// Configuración del drawer/sidebar
export const DRAWER_CONFIG = {
  WIDTH: 280,
  MINI_WIDTH: 60,
  TRANSITION_DURATION: 300,
};

// URLs de ayuda y soporte
export const SUPPORT_CONFIG = {
  HELP_URL: import.meta.env.VITE_HELP_URL || "https://empresa.com/sgcm/help",
  SUPPORT_EMAIL: import.meta.env.VITE_SUPPORT_EMAIL || "soporte@empresa.com",
  DOCUMENTATION_URL: "https://empresa.com/sgcm/docs",
};

// Configuración de desarrollo
export const DEV_CONFIG = {
  ENABLE_REDUX_DEVTOOLS: import.meta.env.NODE_ENV === "development",
  LOG_LEVEL: import.meta.env.NODE_ENV === "development" ? "debug" : "error",
  MOCK_DELAYS: import.meta.env.NODE_ENV === "development",
};

// Regex patterns comunes
export const REGEX_PATTERNS = {
  EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE: /^[\+]?[1-9][\d]{0,15}$/,
  CODE: /^[A-Z0-9\-_]+$/,
  NAME: /^[a-zA-ZÀ-ÿ\u00f1\u00d1\s]+$/,
  ALPHANUMERIC: /^[a-zA-Z0-9\s]+$/,
};
